import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { getHotPotatoes, getHotPotatoReactionsBatch, hotPotatoGraphicUrl, toggleHotPotatoReaction } from '../api/hotPotato';
import { getAllowedEmojis } from '../api/reactions';
import { getAccount } from '../api/auth';
import { HotPotatoIcon } from '../assets/icons';
import ReactionBar from '../components/ReactionBar';
import '../styles/HotPotato.css';

const formatWhen = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    const sameYear = date.getFullYear() === new Date().getFullYear();
    return date.toLocaleDateString(undefined, sameYear
        ? { month: 'short', day: 'numeric' }
        : { month: 'short', day: 'numeric', year: 'numeric' });
};

const HotPotatoHistoryPage = () => {
    const history = useHistory();
    const [tab, setTab] = useState('received');
    const [received, setReceived] = useState([]);
    const [sent, setSent] = useState([]);
    const [reactions, setReactions] = useState({});
    const [currentUserId, setCurrentUserId] = useState(null);
    const [allowedEmojis, setAllowedEmojis] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const [result, account, emojis] = await Promise.all([
                    getHotPotatoes(),
                    getAccount().catch(() => null),
                    getAllowedEmojis().catch(() => []),
                ]);
                if (!mounted) return;
                const receivedItems = result.received || [];
                const sentItems = result.sent || [];
                setReceived(receivedItems);
                setSent(sentItems);
                setCurrentUserId(account?.user?.auth_id || null);
                setAllowedEmojis(emojis || []);

                const ids = [...new Set(
                    [...receivedItems, ...sentItems].map((hp) => hp.id).filter(Boolean)
                )];
                if (ids.length > 0) {
                    try {
                        const reactionData = await getHotPotatoReactionsBatch(ids);
                        if (mounted) setReactions(reactionData.reactions || {});
                    } catch (err) {
                        console.error('Failed to fetch reactions:', err);
                    }
                }
            } catch (err) {
                if (mounted) setError(err.message || 'Failed to load Hot Potatoes');
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    const refreshReactions = async (hotPotatoId) => {
        try {
            const data = await getHotPotatoReactionsBatch([hotPotatoId]);
            setReactions((prev) => ({
                ...prev,
                [hotPotatoId]: data.reactions[hotPotatoId] || [],
            }));
        } catch (err) {
            console.error('Failed to refresh reactions:', err);
        }
    };

    const unreadCount = received.filter((hp) => !hp.viewed_at).length;
    const items = tab === 'received' ? received : sent;

    const renderShell = (children) => (
        <div className="sc-page hp-page">
            <div className="sc-container">
                <header className="sc-header hp-header">
                    <button type="button" className="sc-back-link" onClick={() => history.goBack()}>
                        ← Back
                    </button>
                    <h2>
                        <HotPotatoIcon aria-hidden="true" className="hp-title-icon" />
                        Hot Potatoes
                    </h2>
                    <button
                        type="button"
                        className="sc-btn sc-btn--primary sc-btn--sm"
                        onClick={() => history.push('/hot-potato/send')}
                    >
                        Send
                    </button>
                </header>
                {children}
            </div>
        </div>
    );

    if (loading) {
        return renderShell(
            <div className="sc-loading">
                <div className="sc-spinner" />
                <p>Loading Hot Potatoes…</p>
            </div>
        );
    }

    return renderShell(
        <div className="hp-body">
            {error && <div className="sc-alert sc-alert--error" role="alert">{error}</div>}

            <div className="hp-tabs" role="tablist" aria-label="Hot Potato history">
                <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'received'}
                    className={`hp-tab ${tab === 'received' ? 'is-active' : ''}`}
                    onClick={() => setTab('received')}
                >
                    Received
                    <span className="hp-tab-count">{received.length}</span>
                    {unreadCount > 0 && <span className="hp-tab-badge" aria-label={`${unreadCount} new`}>{unreadCount}</span>}
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'sent'}
                    className={`hp-tab ${tab === 'sent' ? 'is-active' : ''}`}
                    onClick={() => setTab('sent')}
                >
                    Sent
                    <span className="hp-tab-count">{sent.length}</span>
                </button>
            </div>

            {items.length === 0 ? (
                <div className="hp-empty-state">
                    <span className="hp-empty-icon" aria-hidden="true">🥔</span>
                    <p>{tab === 'received' ? 'No Hot Potatoes yet.' : "You haven't sent a Hot Potato yet."}</p>
                    <p className="hp-empty-sub">
                        {tab === 'received'
                            ? 'When a friend tosses you one, it lands here.'
                            : 'Visit a friend\'s profile or the SpudMarket to send one.'}
                    </p>
                </div>
            ) : (
                <ul className="hp-list">
                    {items.map((hp) => {
                        const isNew = tab === 'received' && !hp.viewed_at;
                        const who = tab === 'received'
                            ? `From ${hp.sender_username || 'Unknown'}`
                            : `To ${hp.recipient_username || 'Unknown'}`;
                        return (
                            <li key={hp.id} className={`hp-card ${isNew ? 'hp-card--new' : ''}`}>
                                <button
                                    type="button"
                                    className="hp-card-open"
                                    onClick={() => history.push(`/hot-potato/${hp.id}`)}
                                >
                                    <img className="hp-card-graphic" src={hotPotatoGraphicUrl(hp.graphic)} alt="" />
                                    <span className="hp-card-body">
                                        <span className="hp-card-who">{who}</span>
                                        <span className="hp-card-message">{hp.message}</span>
                                        <span className="hp-card-when">{formatWhen(hp.created_at)}</span>
                                    </span>
                                    {isNew && <span className="hp-card-dot" aria-hidden="true" />}
                                </button>
                                <ReactionBar
                                    activityId={hp.id}
                                    reactions={reactions[hp.id] || []}
                                    currentUserId={currentUserId}
                                    allowedEmojis={allowedEmojis}
                                    onToggle={(emoji) => toggleHotPotatoReaction(hp.id, emoji)}
                                    onReactionChange={refreshReactions}
                                />
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default HotPotatoHistoryPage;
